import React from "react";
import moment from "moment";

import EditableCell from "@tailormed/common-client/component/inline-edit/EditableCell";
import DropdownEditor from "@tailormed/common-client/component/inline-edit/editors/DropdownEditor";
import TextEditor from "@tailormed/common-client/component/inline-edit/editors/TextEditor";
import DateEditor from "@tailormed/common-client/component/inline-edit/editors/DateEditor";
import InlineEmptyPlusButton from "@tailormed/common-client/component/inline-edit/InlineEmptyPlusButton";

import { isEditableEnrollmentColumn } from "./inlineEditConfig";
import { APPLICATIONS } from "../../../constant/applications";
import { isMaxBenefitRequiredForRecord } from "../../../utils/programSubtypes";
import EnrollmentValidThroughInlineEditor from "./EnrollmentValidThroughInlineEditor";
import EnrollmentApprovedDateInlineEditor from "./EnrollmentApprovedDateInlineEditor";
import AssigneesInlineEditor, { buildAssigneeRequiredValidator } from "./AssigneesInlineEditor";
import { EnrollmentAssigneeColumn } from "../CommonColumns";
import { StatusPill } from "../../TableCells";
import { dateFormatter } from "../../../utils/date";
import { formatToDecimal, toAssigneeIdArray, formatAssigneeIdsToDisplay } from "../../../utils/formaters";
import {
  ENROLLMENT_STATUS_COLORS,
  getStatusTagStyle,
  getStatusTagHoverBackground,
  getStatusTagHoverBorder
} from "../../../util/statusTagStyles";

// Mirrors track-tab visibility: approval-only fields stay locked outside these states.
const approvedLikeStatuses = [
  APPLICATIONS.statuses.approved,
  APPLICATIONS.statuses.temporarilyApproved,
  APPLICATIONS.statuses.exhausted,
  APPLICATIONS.statuses.expired
];

const appliedOrLaterStatuses = [
  APPLICATIONS.statuses.applied,
  APPLICATIONS.statuses.appliedToWaitlist,
  APPLICATIONS.statuses.missingInformation,
  ...approvedLikeStatuses,
  APPLICATIONS.statuses.rejected,
  APPLICATIONS.statuses.canceled,
  APPLICATIONS.statuses.discarded,
  APPLICATIONS.statuses.ineligible,
  APPLICATIONS.statuses.notInterested,
  APPLICATIONS.statuses.notReturned
];

export const buildEnrollmentInlineEditCellRenderer = ({
  t,
  commitEnrollment,
  onEditStart,
  renderWithLink,
  enrollmentStatusOptions = [],
  getUserOrGroupById = () => null,
  // Per-table because upstream `useStatuses` keying differs between AllEnrollments and other views.
  resolveStatusLabel = (status) => status
}) => {
  const buildCell = (columnKey, record) => {
    switch (columnKey) {
      case "applicationStatus": {
        const currentStatus = record.applicationStatus;
        const statusDisplayName = currentStatus ? resolveStatusLabel(currentStatus) : null;
        // Inject the current status as a disabled option when it isn't selectable (e.g. system-derived
        // Expired/Exhausted) so the editor renders it as a styled pill instead of the raw value.
        const statusOptions =
          currentStatus && !enrollmentStatusOptions.some((o) => String(o.value) === String(currentStatus))
            ? [{ value: currentStatus, label: statusDisplayName, disabled: true }, ...enrollmentStatusOptions]
            : enrollmentStatusOptions;
        return {
          editor: (
            <DropdownEditor
              options={statusOptions}
              listHeight={180}
              placeholder={t("inline_edit.status_placeholder")}
              showArrow={false}
              suffixIcon={null}
              optionRender={(opt) => (
                <span
                  className="tm-status-pill-option"
                  style={{
                    ...getStatusTagStyle(opt.label, ENROLLMENT_STATUS_COLORS),
                    "--tm-status-hover-bg": getStatusTagHoverBackground(opt.label, ENROLLMENT_STATUS_COLORS),
                    "--tm-status-hover-border": getStatusTagHoverBorder(opt.label, ENROLLMENT_STATUS_COLORS)
                  }}
                >
                  {opt.label}
                </span>
              )}
            />
          ),
          valueForEdit: record.applicationStatus,
          displayNode: statusDisplayName ? (
            <StatusPill label={statusDisplayName} colorMap={ENROLLMENT_STATUS_COLORS} />
          ) : null
        };
      }
      case "validThrough": {
        const hasValue = record.effectiveIndefinitely || moment(record.approvalValidThrough).isValid();
        const displayText = record.effectiveIndefinitely
          ? t("patientApplications.indefinitely")
          : moment(record.approvalValidThrough).isValid()
          ? dateFormatter(record.approvalValidThrough)
          : null;
        const isLocked = !approvedLikeStatuses.includes(record.applicationStatus);
        return {
          editor: (
            <EnrollmentValidThroughInlineEditor
              value={record.approvalValidThrough ? moment(record.approvalValidThrough) : null}
              effectiveIndefinitely={record.effectiveIndefinitely}
            />
          ),
          valueForEdit: record.approvalValidThrough,
          displayNode: hasValue ? displayText : null,
          emptyHoverNode: (
            <InlineEmptyPlusButton
              testId={`inline-edit-empty-enrollment-validThrough-${record.applicationId}`}
              ariaLabel={t("patientApplications.valid_through")}
            />
          ),
          disabled: isLocked,
          disabledTooltip: isLocked ? t("inline_edit.valid_through_locked_by_status") : null
        };
      }
      case "approvedAmount": {
        // newTrackTab parity: a canceled application that was approved (has approvedDate) keeps
        // Max Benefit editable.
        const wasApprovedThenCanceled =
          record.applicationStatus === APPLICATIONS.statuses.canceled && !!record.approvedDate;
        const isLocked = !approvedLikeStatuses.includes(record.applicationStatus) && !wasApprovedThenCanceled;
        // Track-tab parity: Max Benefit is required except for free-drug programs.
        const isMaxBenefitRequired = isMaxBenefitRequiredForRecord(record);
        return {
          editor: (
            <TextEditor
              prefix="$"
              maxLength={15}
              parse={(v) => (v || "").replace(/[^\d.]/g, "")}
              validate={(v) => {
                if (!v) return isMaxBenefitRequired ? t("patientApplications.max_benefit_required") : true;
                return /^\d+(\.\d{0,2})?$/.test(v) ? true : t("inline_edit.amount_invalid");
              }}
            />
          ),
          valueForEdit: record.approvedAmount != null ? String(record.approvedAmount) : "",
          displayNode: record.approvedAmount ? `$${formatToDecimal(record.approvedAmount)}` : null,
          emptyHoverNode: (
            <InlineEmptyPlusButton
              testId={`inline-edit-empty-enrollment-approvedAmount-${record.applicationId}`}
              ariaLabel={t("patientApplications.max_benefit")}
            />
          ),
          disabled: isLocked,
          disabledTooltip: isLocked ? t("inline_edit.max_benefit_locked_by_status") : null
        };
      }
      case "appliedDate": {
        const isLocked = !appliedOrLaterStatuses.includes(record.applicationStatus);
        return {
          editor: <DateEditor />,
          valueForEdit: record.appliedDate ? moment(record.appliedDate) : null,
          displayNode: record.appliedDate ? dateFormatter(record.appliedDate) : null,
          emptyHoverNode: (
            <InlineEmptyPlusButton
              testId={`inline-edit-empty-enrollment-appliedDate-${record.applicationId}`}
              ariaLabel={t("patientApplications.applied_date")}
            />
          ),
          disabled: isLocked,
          disabledTooltip: isLocked ? t("inline_edit.applied_date_locked_by_status") : null
        };
      }
      case "approvedDate": {
        const isLocked = !approvedLikeStatuses.includes(record.applicationStatus);
        return {
          editor: (
            <EnrollmentApprovedDateInlineEditor
              value={record.approvedDate ? moment(record.approvedDate) : null}
              effectiveIndefinitely={record.effectiveIndefinitely}
            />
          ),
          valueForEdit: record.approvedDate,
          displayNode: record.approvedDate ? dateFormatter(record.approvedDate) : null,
          emptyHoverNode: (
            <InlineEmptyPlusButton
              testId={`inline-edit-empty-enrollment-approvedDate-${record.applicationId}`}
              ariaLabel={t("patientApplications.approved_date")}
            />
          ),
          disabled: isLocked,
          disabledTooltip: isLocked ? t("inline_edit.approved_date_locked_by_status") : null
        };
      }
      case "member_id":
        return { displayNode: record.memberId || null };
      case "enrollmentAssignee": {
        // Multi-assignee (track-tab parity): the field may hold an array or a comma-separated
        // id string. Only render the column when at least one id resolves to a known name, so
        // orphan-only rows show the empty `+` instead of leaking raw ids into the editor.
        const idArray = toAssigneeIdArray(record.enrollmentAssignee);
        const hasNamedAssignee = Boolean(formatAssigneeIdsToDisplay(idArray, getUserOrGroupById));
        return {
          editor: (
            <AssigneesInlineEditor
              // Track-tab parity: an enrollment must keep at least one assignee. Reported under the
              // field like Max Benefit, so the editor stays open instead of reverting.
              validate={buildAssigneeRequiredValidator(t)}
            />
          ),
          valueForEdit: idArray,
          displayNode: hasNamedAssignee ? <EnrollmentAssigneeColumn record={record} /> : null,
          emptyHoverNode: (
            <InlineEmptyPlusButton
              testId={`inline-edit-empty-enrollment-enrollmentAssignee-${record.applicationId}`}
              ariaLabel={t("inline_edit.assignee_placeholder")}
            />
          )
        };
      }
      default:
        return null;
    }
  };

  return (columnKey, record) => {
    const cell = buildCell(columnKey, record);
    if (!cell || !isEditableEnrollmentColumn(columnKey)) {
      return renderWithLink(record, () => cell?.displayNode ?? null);
    }
    return renderWithLink(record, () => (
      <EditableCell
        value={cell.valueForEdit}
        displayNode={cell.displayNode}
        emptyHoverNode={cell.emptyHoverNode}
        editor={cell.editor}
        onCommit={(newValue) => commitEnrollment(record, columnKey, newValue)}
        onEditStart={() => onEditStart && onEditStart(columnKey)}
        disableHoverFrame={cell.disableHoverFrame}
        disabled={cell.disabled}
        disabledTooltip={cell.disabledTooltip}
        testId={`inline-edit-enrollment-${columnKey}-${record.applicationId}`}
      />
    ));
  };
};
